import Big from 'big.js';
import * as models from 'smg/admin/models';
import axios from 'smg/axios';

export interface CartWatcherHandle {
    // Pause polling. The place-order submit handler calls this before
    // starting any persistent writes so a mid-submit poll can't reload
    // the page while the POST is in flight.
    stop(): void;
    // Resume polling after a previous stop(). The place-order submit
    // pairs this with stop() on its failure path so an abandoned (failed)
    // submit doesn't leave the page with stale-cart detection permanently
    // off. No-op if polling is already running.
    start(): void;
}

export interface CartWatcherDeps {
    cartID: string;
    // Subtotal captured at page load. Subtotal is server-computed from the
    // line items alone — shipping/tax/promo/override are all client-driven
    // fetch params, so they don't move this number. That makes subtotal
    // the right "did the cart itself change?" signal: a quantity edit, a
    // line item add/remove, or a pricing rebuild in another tab moves it;
    // the admin's local UI adjustments here don't.
    initialSubtotal: InstanceType<typeof Big>;
}

// 30s matches the customer-facing express checkout (express/ts/pages/checkout.ts
// startCartPolling). Frequent enough that a stale cart is caught within one
// minute of editing it in another tab, infrequent enough not to hammer the
// admin API for an idle checkout tab.
const POLL_INTERVAL_MS = 30_000;

export function SetupCartWatcher(deps: CartWatcherDeps): CartWatcherHandle {
    const initial = deps.initialSubtotal.toString();
    let interval: ReturnType<typeof setInterval> | undefined;
    let stopped = false;

    async function tick() {
        if (stopped) {
            return;
        }
        try {
            // Minimal params — match the initial GetCheckoutCart() fetch so
            // the returned subtotal reflects the cart's own state, not the
            // admin's local pricing inputs (address/promo/override). Those
            // are only relevant to the displayed totals, not to "did
            // anything change about the cart itself?"
            const resp = await axios.get(`/v2/carts/${deps.cartID}`, { params: { data: 'id,cartItems' } });
            // Re-check after the await — stop() may have been called by the
            // place-order submit while this request was in flight. Without
            // this guard a tick can still fire window.location.reload()
            // mid-POST and strand the submit.
            if (stopped) {
                return;
            }
            const cart = models.CartPreviewResponse.fromJSON(resp.data);
            if (!cart.cart.cartItems.length) {
                // Cart was emptied / deleted / deactivated in another tab.
                // Reloading lets pages/checkout.ts's GetCheckoutCart redirect
                // path render the empty state.
                window.location.reload();
                return;
            }
            if (cart.subtotal.toString() !== initial) {
                window.location.reload();
            }
        } catch {
            // Ignore transient failures — next tick retries. Express does
            // the same; persistent failures are surfaced by the user's own
            // address/promo edits, not by the watcher.
        }
    }

    function start() {
        if (interval !== undefined) {
            return;
        }
        stopped = false;
        interval = setInterval(() => void tick(), POLL_INTERVAL_MS);
    }

    function stop() {
        stopped = true;
        if (interval !== undefined) {
            clearInterval(interval);
            interval = undefined;
        }
    }

    start();

    return { stop, start };
}
